// MACHINERY V2 (Stroje) — the katalog card's READINESS ROW, below 1280 only
// (M2.16c).
//
// >>> WHY ITS OWN FILE. <<< CatalogModelCardMobile.tsx came in at 339 of the
// project's 300-line cap, and this row is the one block in it that is a concept
// rather than a slice of layout: it is the surface that AMENDS a locked rule,
// and it carries that rule's reasoning. Splitting here keeps the card a card.
//
// ============================================================
//  THE LOCK THIS IS AN AMENDMENT TO
// ============================================================
// `readiness/CapabilityBadge.tsx:9-11` locks "on fleet + catalog cards there is
// NO readiness text row". That lock is AMENDED, BELOW 1280 ONLY, by the manager
// ruling of 2026-08-25, and the amendment is written into the lock itself
// (CapabilityBadge.tsx:13-25) so the next reader finds ONE rule rather than a
// rule and a contradicting surface. The short form: the lock's justification is
// "these two badges plus their HOVER TOOLTIP carry the whole signal", and
// CapabilityBadge.tsx:36-38 records that Radix tooltips do not open on touch —
// so on a phone half the locked mechanism never existed. This row restores the
// lock's INTENT (readiness legible without hover) on exactly the tier where its
// letter cannot operate. Desktop keeps badges-only, unamended.
//
// >>> AND THE ROW IS THE DISCLOSURE TRIGGER, because otherwise the popup would
// be unreachable here. <<< On desktop the badge opens ReadinessPopupV2; this
// card renders no badge, so the row takes that job. Gated on the SAME
// `presentation.interactive` flag the badge is gated on — never a second
// condition — so the "connected = static, nothing clickable" rule cannot drift
// on this surface. On the katalog `ctx` is omitted, so `connected` is always
// false and `interactive` is always true; reading the flag anyway is what keeps
// that true by construction rather than by luck.
//
// >>> THE TIER COMES FROM readinessTier.ts, THE ONE PLACE IT EXISTS. <<< Nothing
// here re-tests a raw readiness field, and the legend explaining these levels
// renders from the same table — so the card cannot name a level the legend
// describes differently.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import {
  READINESS_TIER_DOT_SIZE,
  readinessTierDescriptor,
} from "../readiness/readinessTier";

export interface CatalogCardReadinessRowProps {
  presentation: MachineryV2ReadinessPresentation;
  onOpenReadiness: () => void;
  /** Swallows Enter/Space so a keypress on this control cannot reach the
   *  navigating card. Owned by the card, because the card is the host that
   *  would otherwise navigate. */
  onKeyDown: (e: React.KeyboardEvent) => void;
  /** stopPropagation + run, the card's shared mouse-path guard. */
  onActivate: (e: React.SyntheticEvent, run: () => void) => void;
}

const CatalogCardReadinessRow: React.FC<CatalogCardReadinessRowProps> = ({
  presentation,
  onOpenReadiness,
  onKeyDown,
  onActivate,
}) => {
  const { translate } = useTranslation("myMachinery");
  const tier = readinessTierDescriptor(presentation);
  const tierLabel = translate(tier.labelKey);
  const interactive = presentation.interactive;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        marginTop: 10,
        paddingTop: 2,
        borderTop: "1px solid var(--border-soft)",
        minWidth: 0,
      }}
    >
      <button
        type="button"
        onClick={(e) =>
          onActivate(e, () => {
            if (interactive) onOpenReadiness();
          })
        }
        onKeyDown={onKeyDown}
        aria-haspopup={interactive ? "dialog" : undefined}
        aria-label={`${translate("myMachinery:midasReadiness")}: ${tierLabel}`}
        style={{
          // 44px target, transparent — the row's own text is what is seen.
          // Same construction as mobile/TouchChip.tsx.
          height: 44,
          minWidth: 44,
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          flex: 1,
          border: 0,
          background: "transparent",
          padding: 0,
          cursor: interactive ? "pointer" : "default",
          fontFamily: "inherit",
          textAlign: "left",
        }}
      >
        <span
          aria-hidden
          style={{
            width: READINESS_TIER_DOT_SIZE,
            height: READINESS_TIER_DOT_SIZE,
            borderRadius: 999,
            background: tier.ink,
            flexShrink: 0,
            display: "inline-block",
          }}
        />
        <span
          style={{
            fontSize: 11,
            color: "var(--ink-2)",
            fontWeight: 600,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {tierLabel}
        </span>
      </button>

      {/* Decorative: the whole card is the button that does this. */}
      <span
        aria-hidden
        style={{
          fontSize: 12.5,
          color: "var(--mod-accent)",
          fontWeight: 600,
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}
      >
        {translate("myMachinery:viewDetails")} ›
      </span>
    </div>
  );
};

export default CatalogCardReadinessRow;
